/*
  mediaCompressor.js
  A lightweight, cache-aware image compressor for React Native (iOS/Android),
  designed to be called only at commit time (e.g., “Save to Trippin”, “Post Flash”).

  Key rules implemented:
  - Never overwrite the original image; UI should always render the original.
  - Creates a single compressed copy in cache/temp storage and returns its URI + metadata.
  - Deterministic cache key: repeated calls with the same inputUri + options skip recompression.

  Library: react-native-compressor (https://github.com/Shobbak/react-native-compressor)
  Platforms: Bare React Native. Capacitor notes/fallbacks included in comments for future wiring.

  Usage in a component:
    import { compressImage } from "./mediaCompressor";

    async function onCommit(uri) {
      const { uri: compressedUri, width, height, size } = await compressImage(uri, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
        format: "JPEG",
      });
      // Upload compressedUri via your presigned URL logic
    }

  Recommended quality vs size trade-offs:
  - maxWidth: 1280 or 1920 are practical for social images; larger adds size with limited visible gain on mobile.
  - maxHeight: mirror maxWidth or preserve aspect ratio using the library (we pass both to bound the result).
  - quality: 0.7–0.9 is a good sweet spot. 0.8 often cuts size 60–80% with minimal perceptual loss.
  - format: JPEG is widely compatible; WEBP can be smaller but consider iOS 14- support if you ever need decoding client-side.

  Future video compression:
  - Implemented below via react-native-compressor on RN. On web/Capacitor (no RN), this falls back to a pass-through
    (no-op) unless you wire ffmpeg-based compression. The API remains the same so you can upgrade later.

  Notes for Capacitor apps (non-RN web runtime):
  - This module targets React Native. If your app is a web React app wrapped by Capacitor without RN,
    the react-native-compressor package won’t be available. In that case, create a platform adapter and
    use @capacitor/filesystem + a web image compressor (e.g., browser-image-compression) on web, and a native plugin
    on iOS/Android. See TODO comments below for wiring a Capacitor path.
*/

/**
 * @typedef {Object} CompressOptions
 * @property {number=} maxWidth   - Maximum width in pixels (recommended: 1280 or 1920)
 * @property {number=} maxHeight  - Maximum height in pixels (recommended: 1280 or 1920)
 * @property {number=} quality    - Float in [0,1] (recommended: 0.7–0.9)
 * @property {"JPEG"|"WEBP"=} format - Output format
 */

/**
 * Result shape for compressed image.
 * @typedef {Object} CompressResult
 * @property {string} uri    - local file:// URI or absolute path depending on platform
 * @property {number} width  - resulting width in pixels
 * @property {number} height - resulting height in pixels
 * @property {number} size   - file size in bytes
 */

// -------- Internal utilities (RN-focused with safe dynamic requires) --------

const isReactNative = () =>
  typeof navigator !== "undefined" && navigator.product === "ReactNative";
const isWebLike = () => typeof window !== 'undefined' && !isReactNative();

// Lightweight logger
const log = (...args) => {
  try { console.log('[mediaCompressor]', ...args); } catch (_) {}
};
const warn = (...args) => {
  try { console.warn('[mediaCompressor]', ...args); } catch (_) {}
};
const errorLog = (...args) => {
  try { console.error('[mediaCompressor]', ...args); } catch (_) {}
};

const tryRequire = (name) => {
  try {
    // eslint-disable-next-line no-eval
    return eval('require')(name);
  } catch (_) {
    return null;
  }
};

/** Simple, fast non-cryptographic hash (FNV-1a 32-bit) for cache keys */
const fnv1a32 = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // 32-bit FNV prime: 16777619
    h = (h >>> 0) * 0x01000193;
  }
  // Convert to 8-char hex
  return (h >>> 0).toString(16).padStart(8, '0');
};

const normalizeOptions = (options = {}) => {
  const norm = {
    maxWidth: options.maxWidth ?? 1920,
    maxHeight: options.maxHeight ?? 1920,
    quality: typeof options.quality === 'number' ? Math.min(Math.max(options.quality, 0), 1) : 0.8,
    format: (options.format === 'WEBP' ? 'WEBP' : 'JPEG'),
  };
  return norm;
};

const getExtForFormat = (format) => (format === 'WEBP' ? 'webp' : 'jpg');

const buildCacheKey = (inputUri, options) => {
  const norm = normalizeOptions(options);
  const keyStr = JSON.stringify({
    src: inputUri,
    maxWidth: norm.maxWidth,
    maxHeight: norm.maxHeight,
    quality: norm.quality,
    format: norm.format,
  });
  return fnv1a32(keyStr);
};

const ensureLeadingFileScheme = (p) => (p.startsWith('file://') ? p : `file://${p}`);
const stripFileScheme = (p) => (p.startsWith('file://') ? p.slice('file://'.length) : p);

// In-memory map to dedupe concurrent compress requests for the same key
const inFlight = new Map();
// In-memory cache for web fallbacks (Blob URLs and metadata)
const webCache = new Map();

// RNFS helpers (only resolved at runtime on RN)
const rnfsApi = () => {
  const RNFS = tryRequire('react-native-fs');
  if (!RNFS) return null;
  const baseCache = RNFS.CachesDirectoryPath || RNFS.TemporaryDirectoryPath;
  const cacheRoot = `${baseCache}/trippin_media_cache`;
  const pathJoin = (...parts) =>
    parts
      .filter(Boolean)
      .join('/')
      .replace(/\\/g, '/')      // backslashes to forward slashes
      .replace(/\/\/{2,}/g, '/'); // collapse duplicate slashes

  const ensureDir = async (dir) => {
    const exists = await RNFS.exists(dir);
    if (!exists) {
      await RNFS.mkdir(dir);
    }
  };

  const exists = (p) => RNFS.exists(p);
  const stat = (p) => RNFS.stat(p);
  const moveFile = (src, dest) => RNFS.moveFile(src, dest);

  return { RNFS, cacheRoot, pathJoin, ensureDir, exists, stat, moveFile };
};

// Capacitor helpers (only if you decide to enable a non-RN runtime)
// NOTE: Not wired by default; left as guidance for future integration.
const capacitorFsApi = async () => {
  // Example wiring if you run Capacitor without RN:
  // const { Filesystem, Directory } = await import('@capacitor/filesystem');
  // return { Filesystem, Directory };
  return null;
};

/**
 * Attempts to get image dimensions using RN Image.getSize.
 * Falls back to undefined if not available.
 * @param {string} uri - file:// URI preferred
 * @returns {Promise<{width:number,height:number}|null>}
 */
const getImageSizeRN = async (uri) => {
  const RN = tryRequire('react-native');
  if (!RN || !RN.Image) return null;
  const { Image } = RN;
  const fileUri = ensureLeadingFileScheme(uri);
  return new Promise((resolve) => {
    Image.getSize(
      fileUri,
      (width, height) => resolve({ width, height }),
      () => resolve(null)
    );
  });
};

/**
 * Compresses an image and caches the result deterministically based on inputUri+options.
 * Ensures the original is never modified.
 *
 * @param {string} inputUri      - Source image URI (e.g., file:// path from picker or camera)
 * @param {CompressOptions=} options
 * @returns {Promise<CompressResult>}
 */
export async function compressImage(inputUri, options = {}) {
  if (!inputUri || typeof inputUri !== 'string') {
    throw new Error('compressImage: inputUri must be a non-empty string.');
  }
  const opts = normalizeOptions(options);
  const key = buildCacheKey(inputUri, opts);
  const ext = getExtForFormat(opts.format);
  log('compressImage:start', { uriSnippet: String(inputUri).slice(0, 60), opts, key, platform: isReactNative() ? 'RN' : (isWebLike() ? 'web' : 'unknown') });

  // Web/Capacitor fallback path: use Canvas-based resize + toBlob
  if (isWebLike()) {
    if (webCache.has(key)) return webCache.get(key);

    const work = (async () => {
      // Load image
      const srcBlob = await fetch(inputUri).then(r => r.blob());
      log('compressImage:web:loadedSource', { size: srcBlob.size });
      const imgUrl = URL.createObjectURL(srcBlob);
      try {
        const img = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = (e) => reject(new Error('Failed to load image for compression'));
          image.src = imgUrl;
        });
        const { naturalWidth: w0, naturalHeight: h0 } = img;
        log('compressImage:web:originalDims', { width: w0, height: h0 });
        // Compute target size while preserving aspect ratio
        const ratio = Math.min(1, opts.maxWidth / w0 || 1, opts.maxHeight / h0 || 1);
        const targetW = Math.max(1, Math.round(w0 * ratio));
        const targetH = Math.max(1, Math.round(h0 * ratio));
        log('compressImage:web:targetDims', { width: targetW, height: targetH, ratio });
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetW, targetH);
        const mime = opts.format === 'WEBP' ? 'image/webp' : 'image/jpeg';
        const blob = await new Promise((resolve, reject) =>
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))), mime, opts.quality)
        );
        const outUrl = URL.createObjectURL(blob);
        const result = { uri: outUrl, width: targetW, height: targetH, size: blob.size };
        log('compressImage:web:done', { size: blob.size, uriSnippet: outUrl.slice(0, 60) });
        webCache.set(key, result);
        return result;
      } finally {
        URL.revokeObjectURL(imgUrl);
      }
    })();
    webCache.set(key, work);
    const res = await work;
    webCache.set(key, res);
    return res;
  }

  // React Native path
  const rnfs = rnfsApi();
  if (!rnfs) {
    throw new Error(
      'react-native-fs is required for caching. Please install and link react-native-fs, or add a Capacitor Filesystem adapter.'
    );
  }

  const { RNFS, cacheRoot, pathJoin, ensureDir, exists, stat, moveFile } = rnfs;
  await ensureDir(cacheRoot);

  const cachedFsPath = pathJoin(cacheRoot, `${key}.${ext}`);
  const cachedUri = ensureLeadingFileScheme(cachedFsPath);
  log('compressImage:RN:paths', { cacheRoot, cachedFsPath });

  // If cached copy exists, return metadata and skip recompression
  if (await exists(cachedFsPath)) {
    const sizeInfo = await stat(cachedFsPath);
    const dims = (await getImageSizeRN(cachedUri)) || { width: 0, height: 0 };
    log('compressImage:RN:cacheHit', { size: Number(sizeInfo.size) || 0, dims });
    return {
      uri: cachedUri,
      width: dims.width,
      height: dims.height,
      size: Number(sizeInfo.size) || 0,
    };
  }

  // Dedupe concurrent work: if there's an in-flight promise for this key, await it
  if (inFlight.has(key)) {
    log('compressImage:RN:inFlightDedup', { key });
    return inFlight.get(key);
  }

  const workPromise = (async () => {
  // Dynamic import to avoid bundling in non-RN contexts
  const compressor = tryRequire('react-native-compressor');
  if (!compressor || !compressor.Image) {
    throw new Error(
      'react-native-compressor is missing. Install it and rebuild the native app to enable compression.'
    );
  }

  const { Image: CompressorImage } = compressor;

  // Compose compressor options
  const compressorOpts = {
    maxWidth: opts.maxWidth,
    maxHeight: opts.maxHeight,
    quality: opts.quality,
    compressionMethod: 'manual', // be explicit to honor our max bounds
  };

  // Some versions accept output extension hint; keep it best-effort
  const outputHint = opts.format === 'WEBP' ? 'WEBP' : 'JPEG';
  log('compressImage:RN:compressStart', { compressorOpts, outputHint });

  let tempCompressedUri;
  try {
    tempCompressedUri = await CompressorImage.compress(inputUri, {
      ...compressorOpts,
      output: outputHint.toLowerCase(), // "jpeg" | "webp" if supported
      // returnableOutputType: 'uri' // default behavior: returns URI/path
    });
    log('compressImage:RN:compressResult', { tempCompressedUri: String(tempCompressedUri).slice(0, 80) });
  } catch (e) {
    // Retry without output hint for older versions
    warn('compressImage:RN:retryWithoutOutputHint', { message: e?.message });
    tempCompressedUri = await CompressorImage.compress(inputUri, compressorOpts);
  }

  // react-native-compressor usually returns a file:// or absolute path
  const tempFsPath = stripFileScheme(String(tempCompressedUri));
  log('compressImage:RN:moveToCache', { tempFsPath, cachedFsPath });

  // Move to deterministic cache path (single copy)
  if (tempFsPath !== cachedFsPath) {
    // Ensure temp exists before moving (some implementations already write under Caches)
    const tempExists = await exists(tempFsPath);
    if (tempExists) {
      await moveFile(tempFsPath, cachedFsPath);
    } else {
      // If the library already used our target location, just proceed
      // Otherwise, bail with a helpful error
      const alreadyThere = await exists(cachedFsPath);
      if (!alreadyThere) {
        errorLog('compressImage:RN:missingTempAndCache', { tempFsPath, cachedFsPath });
        throw new Error('Failed to locate compressed output file.');
      }
    }
  }

  const sizeInfo = await stat(cachedFsPath);
  const dims = (await getImageSizeRN(cachedUri)) || { width: 0, height: 0 };
  log('compressImage:RN:done', { size: Number(sizeInfo.size) || 0, dims, uriSnippet: cachedUri.slice(0, 80) });

  return {
    uri: cachedUri,
    width: dims.width,
    height: dims.height,
    size: Number(sizeInfo.size) || 0,
  };
  })();

  inFlight.set(key, workPromise);
  try {
    const result = await workPromise;
    return result;
  } finally {
    inFlight.delete(key);
  }
}

// ----------------------------------------------------------------------------
// Video compression API
// ----------------------------------------------------------------------------

/**
 * @typedef {Object} VideoCompressOptions
 * @property {number=} maxSize   - Longest side in px (e.g., 1080). RN path uses this as a bound.
 * @property {number=} bitrate   - Target bitrate in bps (e.g., 2_000_000 for ~2 Mbps)
 * @property {boolean=} keepAudio - Keep audio track (default true). If false, try to drop audio where supported.
 */

/**
 * Compress a video. On RN, uses react-native-compressor. On web, pass-through (no-op) unless you wire ffmpeg.
 * Caches deterministically based on inputUri + options to avoid recompression.
 *
 * @param {string} inputUri
 * @param {VideoCompressOptions=} options
 * @returns {Promise<CompressResult>}
 */
export async function compressVideo(inputUri, options = {}) {
  if (!inputUri || typeof inputUri !== 'string') {
    throw new Error('compressVideo: inputUri must be a non-empty string.');
  }
  const opts = {
    maxSize: typeof options.maxSize === 'number' ? options.maxSize : 1080,
    bitrate: typeof options.bitrate === 'number' ? options.bitrate : 2_000_000,
    keepAudio: options.keepAudio !== false,
  };

  const key = fnv1a32(JSON.stringify({ src: inputUri, ...opts }));
  log('compressVideo:start', { uriSnippet: String(inputUri).slice(0, 60), opts, key, platform: isReactNative() ? 'RN' : (isWebLike() ? 'web' : 'unknown') });

  // Web/Capacitor fallback: pass-through + metadata
  if (isWebLike()) {
    if (webCache.has(key)) return webCache.get(key);
    const work = (async () => {
      const blob = await fetch(inputUri).then(r => r.blob());
      log('compressVideo:web:loadedSource', { size: blob.size });
      const url = URL.createObjectURL(blob);
      const dims = await (async () => {
        try {
          const v = document.createElement('video');
          v.preload = 'metadata';
          const p = new Promise((resolve, reject) => {
            v.onloadedmetadata = () => resolve({ width: v.videoWidth || 0, height: v.videoHeight || 0 });
            v.onerror = () => resolve({ width: 0, height: 0 });
          });
          v.src = url;
          const res = await p;
          return res;
        } catch {
          return { width: 0, height: 0 };
        }
      })();
      const result = { uri: url, width: dims.width, height: dims.height, size: blob.size };
      log('compressVideo:web:done(pass-through)', { size: blob.size, dims, uriSnippet: url.slice(0, 60) });
      webCache.set(key, result);
      return result;
    })();
    webCache.set(key, work);
    const res = await work;
    webCache.set(key, res);
    return res;
  }

  // React Native path
  const rnfs = rnfsApi();
  if (!rnfs) {
    throw new Error('react-native-fs is required for caching videos on RN.');
  }
  const { cacheRoot, pathJoin, ensureDir, exists, stat, moveFile } = rnfs;
  await ensureDir(cacheRoot);
  const cachedFsPath = pathJoin(cacheRoot, `${key}.mp4`);
  const cachedUri = ensureLeadingFileScheme(cachedFsPath);
  log('compressVideo:RN:paths', { cacheRoot, cachedFsPath });

  if (await exists(cachedFsPath)) {
    const info = await stat(cachedFsPath);
    log('compressVideo:RN:cacheHit', { size: Number(info.size) || 0 });
    return { uri: cachedUri, width: 0, height: 0, size: Number(info.size) || 0 };
  }

  if (inFlight.has(key)) return inFlight.get(key);
  const workPromise = (async () => {
    const compressor = tryRequire('react-native-compressor');
    if (!compressor || !compressor.Video) {
      throw new Error('react-native-compressor Video API missing.');
    }
    const { Video } = compressor;
    let tempOut;
    try {
      tempOut = await Video.compress(inputUri, {
        compressionMethod: 'auto',
        maxSize: opts.maxSize,
        bitrate: opts.bitrate,
        // disableAudio not available in all versions; if supported use keepAudio flag
        // includeAudio: opts.keepAudio !== false,
      });
      log('compressVideo:RN:compressResult', { tempOut: String(tempOut).slice(0, 80) });
    } catch (e) {
      // Retry with minimal options
      warn('compressVideo:RN:retryWithMinimalOptions', { message: e?.message });
      tempOut = await Video.compress(inputUri, { compressionMethod: 'auto', maxSize: opts.maxSize });
    }

    const rnfs2 = rnfsApi();
    const tempFsPath = stripFileScheme(String(tempOut));
    log('compressVideo:RN:moveToCache', { tempFsPath, cachedFsPath });
    if (tempFsPath !== cachedFsPath) {
      const tempExists = await rnfs2.exists(tempFsPath);
      if (tempExists) {
        await rnfs2.moveFile(tempFsPath, cachedFsPath);
      } else {
        const alreadyThere = await rnfs2.exists(cachedFsPath);
        if (!alreadyThere) throw new Error('Failed to locate compressed video output file.');
      }
    }
    const info = await rnfs2.stat(cachedFsPath);
    log('compressVideo:RN:done', { size: Number(info.size) || 0, uriSnippet: cachedUri.slice(0, 80) });
    return { uri: cachedUri, width: 0, height: 0, size: Number(info.size) || 0 };
  })();

  inFlight.set(key, workPromise);
  try {
    const res = await workPromise;
    return res;
  } finally {
    inFlight.delete(key);
  }
}

// ----------------------------------------------------------------------------
// Optional: Future extension for video compression (sketch)
// ----------------------------------------------------------------------------

/**
// export async function compressVideo(inputUri, options = {}) {
//   // Example using react-native-compressor Video API:
//   // const { Video } = require('react-native-compressor');
//   // const result = await Video.compress(inputUri, {
//   //   compressionMethod: 'auto',
//   //   bitrate: 1_000_000, // 1 Mbps example
//   //   maxSize: 1920,      // longest side bound
//   // });
//   // // Then move into the same cache dir using the deterministic key approach
//   // return { uri, width, height, size };
// }
*/
